/** Profile selection is a creation-time seed, not an override for existing projects. */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import {
  getProfileVideoDefault,
  setProfileVideoDefault,
  type ProfileVideoDefaultResponse,
} from '../../api/client'
import { trackInteraction } from '../../analytics/interactionAnalytics'
import { useVideoModels } from '../../hooks/useVideoModels'
import { createNadDebug } from '../../utils/debugLog'
import { normalizeVideoPreferences, videoPreferenceLabel } from '../canvas-v2/videoFamilyPreference'

const log = createNadDebug('dw:profile-video-default:settings')

export default function ProfileVideoDefaultSection() {
  const { t } = useTranslation('settings')
  const { native, byom, defaultModel, isReady, error: catalogError } = useVideoModels()
  const [preference, setPreference] = useState<ProfileVideoDefaultResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<{ kind: 'read' } | { kind: 'request'; message: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getProfileVideoDefault()
      setPreference(result)
      if (result.status === 'load_failed' && result.stored_id === null) {
        setError({ kind: 'read' })
      }
    } catch (cause) {
      setError({ kind: 'request', message: cause instanceof Error ? cause.message : String(cause) })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const nativeIds = useMemo(() => normalizeVideoPreferences(native), [native])
  const enabledByom = useMemo(() => byom.filter(model => model.enabled), [byom])
  const available = useMemo(
    () => new Set([...nativeIds, ...enabledByom.map(model => model.id)]),
    [nativeIds, enabledByom],
  )
  const selected = preference?.stored_id ?? ''
  const storedUnavailable = selected !== '' && (
    preference?.status === 'unavailable' || preference?.status === 'invalid' ||
    (isReady && !available.has(selected))
  )
  // The saved value must stay visibly selected even before catalog readiness
  // (or on a catalog failure); an unmatched controlled <select> shows Auto.
  const showSavedOption = selected !== '' && !available.has(selected)
  const canEdit = !loading && !saving && preference !== null &&
    !(preference.status === 'load_failed' && preference.stored_id === null)

  async function handleChange(modelId: string) {
    if (!canEdit || (modelId !== '' && !isReady)) return
    setSaving(true)
    setError(null)
    try {
      const result = await setProfileVideoDefault(modelId || null)
      setPreference(result)
      log({ event: 'selection_changed', previous: selected || null, next: result.stored_id })
      trackInteraction('settings_default_video_model_changed', {
        model_source: !modelId ? 'auto' : modelId.startsWith('gateway/byom/') ? 'byom' : 'native',
      })
    } catch (cause) {
      setError({ kind: 'request', message: cause instanceof Error ? cause.message : String(cause) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section data-testid="profile-video-default" className="space-y-2">
      <div>
        <h3 className="text-sm font-medium text-foreground">{t('profileVideoDefault.title')}</h3>
        <p className="mt-1 text-xs text-muted-foreground">{t('profileVideoDefault.description')}</p>
      </div>
      <div className="relative">
        <select
          aria-label={t('profileVideoDefault.title')}
          data-testid="profile-video-default-select"
          className="h-9 w-full appearance-none rounded-lg border border-input-border bg-input-bg px-3 pr-9 text-sm text-foreground hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
          value={selected}
          disabled={!canEdit || (catalogError !== null && !selected)}
          onChange={event => { void handleChange(event.target.value) }}
        >
          <option value="">{defaultModel
            ? t('profileVideoDefault.auto', { name: videoPreferenceLabel(defaultModel, byom) })
            : t('profileVideoDefault.autoPending')}</option>
          {showSavedOption && (
            <option value={selected} disabled>{storedUnavailable
              ? t('profileVideoDefault.unavailable', { name: videoPreferenceLabel(selected, byom) })
              : t('profileVideoDefault.checking', { name: videoPreferenceLabel(selected, byom) })}</option>
          )}
          <optgroup label={t('profileVideoDefault.native')}>
            {nativeIds.map(id => <option key={id} value={id} disabled={!isReady}>{videoPreferenceLabel(id, byom)}</option>)}
          </optgroup>
          {enabledByom.length > 0 && (
            <optgroup label={t('profileVideoDefault.myModels')}>
              {enabledByom.map(model => <option key={model.id} value={model.id} disabled={!isReady}>{videoPreferenceLabel(model.id, byom)}</option>)}
            </optgroup>
          )}
        </select>
        <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
      </div>
      {loading && <p className="text-xs text-muted-foreground">{t('profileVideoDefault.loading')}</p>}
      {saving && <p className="text-xs text-muted-foreground">{t('profileVideoDefault.saving')}</p>}
      {storedUnavailable && <p role="status" className="text-xs text-warning">{t('profileVideoDefault.unavailableHint')}</p>}
      {preference?.status === 'load_failed' && preference.stored_id !== null && (
        <p role="status" className="text-xs text-warning">{t('profileVideoDefault.availabilityFailed')}</p>
      )}
      {catalogError && <p role="status" className="text-xs text-warning">{catalogError}</p>}
      {error && <p role="alert" className="text-xs text-destructive">{error.kind === 'read' ? t('profileVideoDefault.readFailed') : error.message}</p>}
      {error && <button type="button" onClick={() => { void load() }} className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20">{t('profileVideoDefault.retry')}</button>}
    </section>
  )
}
